/**
 * Notifications Module
 * Handles email signup form functionality
 *
 * NEW: Uses spot-based subscription model
 */

import { analytics } from './analytics.js';
import { isValidEmail } from '../shared/validation.mjs';
import { logger } from './logger.js';
import { getCurrentSpotSlug } from './state.js';
import { sortSpotsData } from '../shared/location-names.mjs';

// Turnstile CAPTCHA token is stored globally (window.turnstileToken)
// Callbacks are defined inline in index.html to avoid race condition with async Turnstile script

// Cache for spots data
let spotsCache = null;

// Track if cascading event listeners have been attached (to avoid duplicates on re-init)
let cascadeListenersAttached = false;

/**
 * Fetch spots from API (with caching)
 * Exported for use by location panel (ui-selectors.js)
 * Data is pre-sorted: California first, then alphabetical regions/areas/spots
 */
export async function fetchSpots() {
    if (spotsCache) {
        return spotsCache;
    }

    try {
        const response = await fetch('/api/spots');
        if (!response.ok) {
            throw new Error('Failed to fetch spots');
        }
        const data = await response.json();
        // Sort data once at fetch time - all consumers get consistent ordering
        spotsCache = sortSpotsData(data.regions || []);
        return spotsCache;
    } catch (error) {
        logger.error('Failed to fetch spots:', error);
        return [];
    }
}

/**
 * Validate subscription form data
 * Returns error message string if invalid, null if valid
 */
function validateSubscriptionForm(data) {
    // Email validation
    if (!data.email || !data.email.trim()) {
        return 'Please enter your email address.';
    }
    if (!isValidEmail(data.email.trim())) {
        return 'Please enter a valid email address.';
    }

    // Notification type validation
    if (!data.notificationType) {
        return 'Please select a notification type.';
    }

    // Spot validation
    if (!data.spotSlug) {
        return 'Please select a spot.';
    }

    // CAPTCHA validation (Turnstile)
    if (!window.turnstileToken) {
        return 'Please complete the CAPTCHA verification.';
    }

    return null; // Valid
}

/**
 * Initialize the notifications signup form
 */
export function initNotifications() {
    const form = document.getElementById('signup-form');
    if (!form) return;

    // Load spots and populate selectors, pre-selecting the current spot
    // (spot mode sets the slug at init; null in legacy mode is harmless)
    initSpotSelectors(getCurrentSpotSlug());

    // Set up form handlers
    setupNotificationTypeToggle();
    setupThresholdSlider();
    setupFormSubmission();
}

/**
 * Initialize spot selectors with data from API
 * @param {string} preSelectSpotSlug - Optional spot slug to pre-select
 */
async function initSpotSelectors(preSelectSpotSlug = null) {
    const regionSelect = document.getElementById('signup-region');
    const areaSelect = document.getElementById('signup-area');
    const spotSelect = document.getElementById('signup-spot');

    if (!regionSelect || !areaSelect || !spotSelect) return;

    // Show loading state
    regionSelect.innerHTML = '<option value="" disabled selected>Loading...</option>';
    areaSelect.innerHTML = '<option value="" disabled selected>Select area...</option>';
    spotSelect.innerHTML = '<option value="" disabled selected>Select spot...</option>';

    // Fetch spots
    const regions = await fetchSpots();

    if (regions.length === 0) {
        regionSelect.innerHTML = '<option value="" disabled selected>Failed to load regions</option>';
        return;
    }

    // Find the spot to pre-select (if provided)
    let preSelectRegion = null;
    let preSelectArea = null;
    if (preSelectSpotSlug) {
        for (const region of regions) {
            for (const area of region.areas || []) {
                const spot = (area.spots || []).find(s => s.slug === preSelectSpotSlug);
                if (spot) {
                    preSelectRegion = region.id;
                    preSelectArea = area.id;
                    break;
                }
            }
            if (preSelectRegion) break;
        }
    }

    // Populate regions (data is pre-sorted by fetchSpots via sortSpotsData)
    regionSelect.innerHTML = '<option value="" disabled selected>Select region...</option>';
    for (const region of regions) {
        const option = document.createElement('option');
        option.value = region.id;
        option.textContent = region.name;
        if (region.id === preSelectRegion) {
            option.selected = true;
        }
        regionSelect.appendChild(option);
    }

    // Set up cascading selectors (only attach once to avoid duplicate listeners)
    if (!cascadeListenersAttached) {
        regionSelect.addEventListener('change', () => {
            updateAreaOptions(regionSelect.value, regions);
        });

        areaSelect.addEventListener('change', () => {
            const selectedRegion = regions.find(r => r.id === regionSelect.value);
            if (selectedRegion) {
                updateSpotOptions(areaSelect.value, selectedRegion.areas);
            }
        });

        cascadeListenersAttached = true;
    }

    // If we have a pre-selected region, populate areas and select the right one
    if (preSelectRegion) {
        updateAreaOptions(preSelectRegion, regions, preSelectArea);

        // If we have a pre-selected area, populate spots and select the right one
        if (preSelectArea) {
            const selectedRegion = regions.find(r => r.id === preSelectRegion);
            if (selectedRegion) {
                updateSpotOptions(preSelectArea, selectedRegion.areas, preSelectSpotSlug);
            }
        }
    }

    updateDropdownVisibility();
}

/**
 * Update area dropdown options based on selected region
 * @param {string} regionId - Selected region ID
 * @param {Array} regions - All regions data
 * @param {string} preSelectAreaId - Optional area ID to pre-select
 */
function updateAreaOptions(regionId, regions, preSelectAreaId = null) {
    const areaSelect = document.getElementById('signup-area');
    const spotSelect = document.getElementById('signup-spot');

    if (!areaSelect || !spotSelect) return;

    const region = regions.find(r => r.id === regionId);

    // Reset area selector
    areaSelect.innerHTML = '<option value="" disabled selected>Select area...</option>';

    if (region?.areas) {
        // Areas are pre-sorted by fetchSpots via sortSpotsData
        for (const area of region.areas) {
            const option = document.createElement('option');
            option.value = area.id;
            option.textContent = area.name;
            if (area.id === preSelectAreaId) {
                option.selected = true;
            }
            areaSelect.appendChild(option);
        }
    }

    // Reset spot selector
    spotSelect.innerHTML = '<option value="" disabled selected>Select spot...</option>';

    updateDropdownVisibility();
}

/**
 * Update spot dropdown options based on selected area
 * @param {string} areaId - Selected area ID
 * @param {Array} areas - All areas for the selected region
 * @param {string} preSelectSpotSlug - Optional spot slug to pre-select
 */
function updateSpotOptions(areaId, areas, preSelectSpotSlug = null) {
    const spotSelect = document.getElementById('signup-spot');

    if (!spotSelect) return;

    const area = areas.find(a => a.id === areaId);

    // Reset spot selector
    spotSelect.innerHTML = '<option value="" disabled selected>Select spot...</option>';

    if (area?.spots) {
        // Spots are pre-sorted by fetchSpots via sortSpotsData
        for (const spot of area.spots) {
            const option = document.createElement('option');
            option.value = spot.slug;
            option.textContent = spot.name;
            if (spot.slug === preSelectSpotSlug) {
                option.selected = true;
            }
            spotSelect.appendChild(option);
        }
    }

    updateDropdownVisibility();
}

/**
 * Hide dropdowns that have only one real option (excluding placeholder)
 */
function updateDropdownVisibility() {
    const regionSelect = document.getElementById('signup-region');
    const areaSelect = document.getElementById('signup-area');
    const spotSelect = document.getElementById('signup-spot');

    const regionGroup = document.getElementById('signup-region-group');
    const areaGroup = document.getElementById('signup-area-group');
    const spotGroup = document.getElementById('signup-spot-group');

    // Count real options (excluding disabled placeholder)
    const countRealOptions = (select) => {
        if (!select) return 0;
        return Array.from(select.options).filter(opt => !opt.disabled).length;
    };

    if (regionGroup && regionSelect) {
        regionGroup.classList.toggle('hidden', countRealOptions(regionSelect) <= 1);
    }
    if (areaGroup && areaSelect) {
        areaGroup.classList.toggle('hidden', countRealOptions(areaSelect) <= 1);
    }
    if (spotGroup && spotSelect) {
        spotGroup.classList.toggle('hidden', countRealOptions(spotSelect) <= 1);
    }
}

/**
 * Sync signup form location with current page selection
 * Call this when user changes location via the location panel
 */
export function syncSignupFormLocation() {
    // Pre-select the current spot in the subscription form
    const currentSpotSlug = getCurrentSpotSlug();
    initSpotSelectors(currentSpotSlug);
}

/**
 * Set up notification type toggle (daily vs alert)
 */
function setupNotificationTypeToggle() {
    const radios = document.querySelectorAll('input[name="notificationType"]');
    const dailyOptions = document.getElementById('daily-options');
    const alertOptions = document.getElementById('alert-options');

    // Initially hide both conditional sections (no preselection)
    if (dailyOptions) dailyOptions.classList.add('hidden');
    if (alertOptions) alertOptions.classList.add('hidden');

    radios.forEach(radio => {
        radio.addEventListener('change', () => {
            const isDaily = radio.value === 'daily';

            if (dailyOptions) {
                dailyOptions.classList.toggle('hidden', !isDaily);
            }
            if (alertOptions) {
                alertOptions.classList.toggle('hidden', isDaily);
            }
        });
    });
}

/**
 * Set up threshold slider value display and analytics tracking
 */
function setupThresholdSlider() {
    const slider = document.getElementById('signup-threshold');
    const valueDisplay = document.getElementById('threshold-value');

    if (slider && valueDisplay) {
        slider.addEventListener('input', () => {
            valueDisplay.textContent = slider.value;
        });

        // Track threshold changes for analytics (fires on release, not during drag)
        slider.addEventListener('change', () => {
            analytics.formFieldChange('alert_threshold', slider.value);
        });
    }
}

/**
 * Set up signup start tracking (when user begins interacting with form)
 */
function setupSignupStartTracking() {
    const form = document.getElementById('signup-form');
    const emailInput = document.getElementById('signup-email');

    if (!form || !emailInput) return;

    let signupStartTracked = false;

    // Track when user focuses on email field (primary form interaction)
    emailInput.addEventListener('focus', () => {
        if (!signupStartTracked) {
            signupStartTracked = true;
            analytics.signupStart();
        }
    }, { once: true });
}

/**
 * Set up form submission
 */
function setupFormSubmission() {
    const form = document.getElementById('signup-form');
    if (!form) return;

    // Set up signup start tracking
    setupSignupStartTracking();

    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const submitBtn = document.getElementById('signup-submit');
        const btnText = submitBtn?.querySelector('.btn-text');
        const btnLoading = submitBtn?.querySelector('.btn-loading');
        const successEl = document.getElementById('signup-success');
        const errorEl = document.getElementById('signup-error');
        const errorText = document.getElementById('error-text');

        // Reset messages
        if (successEl) successEl.classList.add('hidden');
        if (errorEl) errorEl.classList.add('hidden');

        // Gather form data
        const formData = new FormData(form);
        const data = {
            email: formData.get('email'),
            notificationType: formData.get('notificationType'),
            spotSlug: formData.get('spotSlug'),
            preferredHour: parseInt(formData.get('preferredHour') || '6'),
            minRatingScore: parseInt(formData.get('minRatingScore') || '80'),
            turnstileToken: window.turnstileToken
        };

        // Client-side validation
        const validationError = validateSubscriptionForm(data);
        if (validationError) {
            if (errorEl) {
                errorEl.classList.remove('hidden');
                if (errorText) {
                    errorText.textContent = validationError;
                }
            }
            return;
        }

        // Show loading state
        if (submitBtn) submitBtn.disabled = true;
        if (btnText) btnText.classList.add('hidden');
        if (btnLoading) btnLoading.classList.remove('hidden');

        try {

            // Submit to API
            const response = await fetch('/api/subscribe', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });

            const result = await response.json();

            if (response.ok) {
                // Success
                form.classList.add('hidden');
                if (successEl) {
                    successEl.classList.remove('hidden');
                    const successText = successEl.querySelector('.message-text');
                    if (successText && result.spotName) {
                        successText.textContent = `You're subscribed! You'll receive ${data.notificationType === 'daily' ? 'daily reports' : 'surf alerts'} for ${result.spotName}.`;
                    }
                }
                analytics.signupSuccess(data.notificationType);
                // Reset Turnstile token
                window.turnstileToken = null;
                if (window.turnstile) {
                    window.turnstile.reset();
                }
            } else {
                // Error
                if (errorEl) {
                    errorEl.classList.remove('hidden');
                    if (errorText) {
                        errorText.textContent = result.message || result.error || 'Something went wrong. Please try again.';
                    }
                }
                analytics.signupError(result.message || result.error || 'Unknown error');
                // Reset Turnstile for retry
                window.turnstileToken = null;
                if (window.turnstile) {
                    window.turnstile.reset();
                }
            }
        } catch (error) {
            logger.error('Subscription error:', error);
            if (errorEl) {
                errorEl.classList.remove('hidden');
                if (errorText) {
                    errorText.textContent = 'Network error. Please check your connection and try again.';
                }
            }
            analytics.signupError('Network error');
        } finally {
            // Reset loading state
            if (submitBtn) submitBtn.disabled = false;
            if (btnText) btnText.classList.remove('hidden');
            if (btnLoading) btnLoading.classList.add('hidden');
        }
    });
}
